import { TextField, TextFieldVariants, Typography } from '@mui/material';
import { useRef, useState, type JSX } from 'react';
import { useField, useFormState } from 'react-final-form';
import { Row } from '.';
import { cleanWholeNumberStr, cleanDecimalStr, formatNumber } from '../helpers/utilityFunctions';

interface FormInputNumberProps<T> {
  fieldName: keyof T;
  variant?: TextFieldVariants;
  label?: string;
  labelStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  rightAlignedKey?: boolean;
  extraStyles?: React.CSSProperties;
  required?: boolean;
  validate?: (x: number) => undefined | string;
  disabled?: boolean;
  readonly?: boolean;
  readonlyStyle?: 'compact' | 'standard';
  placeholder?: string;
  initialValue?: number;
  onChange?: (x: number) => void;
  maxLength?: number;
  min?: number;
  max?: number;
  includeCommaDelimiter?: boolean;
  decimalPlaces?: number;
  blurOnEnter?: boolean;
  fixedDecimalsOnBlur?: boolean;
  startAdornment?: JSX.Element;
  endAdornment?: JSX.Element;
  InputProps?: {
    style?: React.CSSProperties;
    inputProps?: {
      style?: React.CSSProperties;
    };
  };
}

export function FormInputNumber<T>(props: FormInputNumberProps<T>): JSX.Element {
  const {
    fieldName,
    variant,
    label,
    required,
    validate,
    disabled,
    readonly,
    readonlyStyle = 'standard',
    placeholder,
    initialValue,
    onChange,
    startAdornment,
    endAdornment,
    InputProps,
    decimalPlaces,
    blurOnEnter,
    fixedDecimalsOnBlur,
  } = props;
  const { maxLength, min, max, includeCommaDelimiter = true } = props;

  const { submitFailed } = useFormState({ subscription: { submitFailed: true } });
  const inputRef = useRef<typeof TextField>(null);
  const [rawValue, setRawValue] = useState<string | null>(null);

  const { input, meta } = useField<number>(fieldName.toString(), {
    initialValue,
    validate: value =>
      !required || value != null ? (validate ? validate(value) : undefined) : 'Required',
    subscription: { value: true, touched: true, error: true },
  });

  const convertToDisplay = (v: number | undefined) => {
    if (!v) return '';

    if (decimalPlaces != null) {
      if (includeCommaDelimiter) {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: fixedDecimalsOnBlur ? decimalPlaces : 0,
          maximumFractionDigits: decimalPlaces,
        });
        return formatter.format(v);
      }
      return fixedDecimalsOnBlur ? v.toFixed(decimalPlaces) : String(v);
    }

    return includeCommaDelimiter ? formatNumber(v) : String(v);
  };

  const handleChange = (v: React.ChangeEvent<HTMLInputElement>) => {
    const raw = v.target.value;
    let newValue: number | undefined;

    if (decimalPlaces != null) {
      const cleaned = cleanDecimalStr(raw, min, max, decimalPlaces);
      setRawValue(cleaned);
      const parsed = parseFloat(cleaned);
      newValue = isNaN(parsed) ? undefined : parsed;
    } else {
      newValue = cleanWholeNumberStr(raw, min, max);
    }

    input.onChange(newValue);
    if (onChange && newValue !== undefined) onChange(newValue);
  };

  const handleBlur = () => {
    setRawValue(null);
    input.onBlur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLElement).blur();
  };

  const value = input.value ?? 0;
  const displayValue = rawValue ?? convertToDisplay(value);
  const inputNumberWidth = Math.max(displayValue.length || placeholder?.length || 1, 1);

  const helperText = meta.touched || submitFailed ? meta.error : undefined;
  const hasError = helperText != null;

  const delimiters = convertToDisplay(value).match(/(,)/g);
  const fixedMaxLength =
    value.toString().length > (maxLength ?? 0) ? value.toString().length : (maxLength ?? 0);
  const maxLengthCalc = maxLength
    ? fixedMaxLength + (delimiters?.length ?? 0) + (decimalPlaces != null ? 1 : 0)
    : 19 + (delimiters?.length ?? 0);

  const rowStyles: React.CSSProperties = {
    alignItems: 'center',
    gap: '20px',
    ...(props.rightAlignedKey && { flexWrap: 'wrap' }),
    ...props.extraStyles,
  };

  const labelStyles: React.CSSProperties = {
    ...(props.rightAlignedKey && { flex: '2 0 10px', justifyContent: 'flex-end' }),
    ...props.labelStyle,
  };

  const inputStyles: React.CSSProperties = {
    ...(props.rightAlignedKey && { flex: '2 0 10px' }),
    ...props.inputStyle,
  };

  const textFieldInputProps = {
    startAdornment,
    endAdornment,
    ...InputProps,
    inputProps: {
      ...InputProps?.inputProps,
      maxLength: maxLengthCalc,
    },
  };

  const isCompactReadonly = (disabled || readonly) && readonlyStyle === 'compact';

  const textFieldSx = isCompactReadonly
    ? {
        '& .MuiInputBase-input': {
          width: `${inputNumberWidth}ch`,
        },
      }
    : undefined;

  const className = `form-input-number${isCompactReadonly ? ' form-input-number--compact' : ''}`;

  return (
    <Row style={rowStyles}>
      {label && (
        <Row style={labelStyles}>
          <Typography variant='body1' color='info.main'>
            {label}
          </Typography>
        </Row>
      )}

      <TextField
        className={className}
        InputProps={textFieldInputProps}
        variant={variant ?? 'standard'}
        style={inputStyles}
        placeholder={placeholder ?? '0'}
        value={displayValue}
        sx={textFieldSx}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={blurOnEnter ? handleKeyDown : undefined}
        disabled={disabled || readonly}
        helperText={helperText}
        error={hasError}
        inputRef={inputRef}
      />
    </Row>
  );
}

export default FormInputNumber;
